/**
 * Five-concept HLTV partner review at the existing demo route.
 * ?legacy=1 retains the live-player installer and its base/playlist overrides.
 * The concept demo uses live playlist data without sending campaign events.
 */
import { useMemo, useState } from "react";

import { HLTVConceptReview } from "./ConceptReview";
import { HLTVConceptEmbed } from "./ConceptEmbed";

import { buildBookmarkletHref } from "./bookmarkletSource";
import {
  Card,
  ConfigInput,
  ConfigLabel,
  ConfigRow,
  Container,
  DragLink,
  Eyebrow,
  Header,
  HelpText,
  InstructionList,
  Page,
  StepLabel,
  StepNumber,
  Subtitle,
  Title,
} from "./styledComponents";

/* Exported so other surfaces (IframeDemo) can default to the same
   branded HLTV playlist the bookmarklet ships, keeping reviewer
   experiences in lock-step across the install page and the hosted
   demo. */
export const DEFAULT_HLTV_PLAYLIST = "6a0fab0109d472da85f07aa2";
const DEFAULT_PLAYLIST = DEFAULT_HLTV_PLAYLIST;

const LegacyHLTVBookmarklet = () => {
  // Seed config from URL params so links can pre-fill staging /
  // alternate playlists for specific pitch contexts. Origin defaults to
  // wherever this install page is being served from — so the bookmarklet
  // points at whatever environment you're already viewing (staging
  // install page → staging iframe, prod → prod, localhost → localhost),
  // without anyone having to remember to swap the hardcoded host. The
  // `?base=` query param still wins when present.
  const initialBase = useMemo(() => {
    const v = new URLSearchParams(window.location.search).get("base");
    return v ?? window.location.origin;
  }, []);
  const initialPlaylist = useMemo(() => {
    const v = new URLSearchParams(window.location.search).get("playlist");
    return v ?? DEFAULT_PLAYLIST;
  }, []);

  const [base, setBase] = useState(initialBase);
  const [playlist, setPlaylist] = useState(initialPlaylist);
  // Advanced config (origin + playlist id) is hidden by default — the
  // visible install page is for HLTV-side readers, not Allstar
  // engineers. Add `?advanced=1` to the URL to reveal the inputs.
  const showAdvanced = useMemo(
    () => new URLSearchParams(window.location.search).get("advanced") === "1",
    [],
  );

  const href = useMemo(
    () => buildBookmarkletHref({ base, playlistId: playlist }),
    [base, playlist],
  );

  return (
    <Page>
      <Container>
        <Header>
          <Eyebrow>Allstar × HLTV</Eyebrow>
          <Title>Preview the branded player on HLTV</Title>
          <Subtitle>
            Drop the Allstar branded playlist into any HLTV page to see how it
            fits alongside live coverage. Pick a spot, the player slots in right
            after it — the original page stays untouched.
          </Subtitle>
        </Header>

        {showAdvanced && (
          <Card>
            <StepLabel>
              <StepNumber>•</StepNumber>
              <span>Advanced configuration</span>
            </StepLabel>
            <ConfigRow>
              <ConfigLabel>Allstar origin</ConfigLabel>
              <ConfigInput
                value={base}
                onChange={(e) => setBase(e.target.value)}
                spellCheck={false}
                placeholder="https://allstar.gg"
              />
            </ConfigRow>
            <ConfigRow>
              <ConfigLabel>Playlist ID</ConfigLabel>
              <ConfigInput
                value={playlist}
                onChange={(e) => setPlaylist(e.target.value)}
                spellCheck={false}
                placeholder="playlist id"
              />
            </ConfigRow>
            <HelpText>
              Override the origin or playlist used by the bookmarklet. The link
              below regenerates as you type.
            </HelpText>
          </Card>
        )}

        <Card>
          <StepLabel>
            <StepNumber>1</StepNumber>
            <span>Drag to your bookmarks bar</span>
          </StepLabel>
          <DragLink
            href={href}
            // Prevent accidental clicks on the install page from running
            // the bookmarklet here (it would inject into our own page).
            onClick={(e) => e.preventDefault()}
            draggable
          >
            ★ Allstar HLTV Demo
          </DragLink>
          <HelpText style={{ marginTop: 14 }}>
            If your bookmarks bar is hidden, press{" "}
            <code
              style={{
                background: "rgba(255,255,255,0.08)",
                padding: "1px 6px",
                borderRadius: 4,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 12.5,
              }}
            >
              ⌘⇧B
            </code>{" "}
            (Mac) or{" "}
            <code
              style={{
                background: "rgba(255,255,255,0.08)",
                padding: "1px 6px",
                borderRadius: 4,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 12.5,
              }}
            >
              Ctrl+Shift+B
            </code>{" "}
            (Win) to show it, then drag the button.
          </HelpText>
        </Card>

        <Card>
          <StepLabel>
            <StepNumber>2</StepNumber>
            <span>Try it on HLTV</span>
          </StepLabel>
          <InstructionList>
            <li>Open any page on HLTV.</li>
            <li>
              Click the <strong>Allstar HLTV Demo</strong> bookmark to enter
              pick mode.
            </li>
            <li>Click an element — the player drops in right after it.</li>
            <li>
              Use the floating panel to resize, type or pick a{" "}
              <strong>Viewer geo</strong> (any ISO-3166 country code — the
              iframe resolves whatever campaign is live for that geo), or pick a
              different spot.
            </li>
            <li>Re-click the bookmark, press Esc, or refresh to remove it.</li>
          </InstructionList>
        </Card>
      </Container>
    </Page>
  );
};

const HLTVBookmarklet = () =>
  new URLSearchParams(window.location.search).get("embed") === "1" ? (
    <HLTVConceptEmbed />
  ) : new URLSearchParams(window.location.search).get("legacy") === "1" ? (
    <LegacyHLTVBookmarklet />
  ) : (
    <HLTVConceptReview />
  );

export default HLTVBookmarklet;
